import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from "@angular/core";
import { IAggregateFilterEvent, IFilterBarTab } from "@app/directives/_resourceFilters/filter-bar/filter-bar.models";
import { TranslationService } from "@app/services/translation.service";
import { copy } from "angular";
import { DrivingStyleMetric, DrivingStyleMetricCategory, DrivingStyleWeightingType } from "../driving-styles.enums";
import { IDrivingStyle, IDrivingStyleMetricMapping } from "../driving-styles.interfaces";
import { DrivingStylesService, IMetricMappingWeightingLevelDetail } from "../driving-styles.service";
import './driving-style-metric-selector.component.scss';

interface IDisplayDrivingStyleMetricMapping extends IDrivingStyleMetricMapping {
    selected?: boolean;
}

@Component({
    selector: 'driving-style-metric-selector',
    template: require('./driving-style-metric-selector.component.html')
})
export class DrivingStyleMetricSelectorComponent implements OnInit, OnChanges {

    @Input() drivingStyle: IDrivingStyle;
    @Input() maxAllowedMetrics: number = 0;
    @Input() weightingType: DrivingStyleWeightingType;
    @Input() availableMetricMappings: IDrivingStyleMetricMapping[];
    @Input() currentMetricMappings: IDrivingStyleMetricMapping[];
    @Output() currentMetricMappingsChange = new EventEmitter<IDrivingStyleMetricMapping[]>();

    @Output() metricMappingClick = new EventEmitter<IDrivingStyleMetricMapping>();

    displayMetricMappings: IDisplayDrivingStyleMetricMapping[];
    filteredMetricMappings: IDisplayDrivingStyleMetricMapping[];

    metricMappingWeightingLevelDetails: IMetricMappingWeightingLevelDetail[];

    filterTabs: IFilterBarTab<IDisplayDrivingStyleMetricMapping>[] = [];

    constructor(
        private readonly translationService: TranslationService,
        private readonly drivingStylesService: DrivingStylesService
    ) { }

    ngOnInit(): void {
        this.prepare();
    }

    ngOnChanges(changes: SimpleChanges): void {
        this.prepare();
    }

    prepare(): void {
        this.metricMappingWeightingLevelDetails = this.drivingStylesService.getMetricMappingWeightingLevelDetails();

        this.createDisplayMetricMappings();
        this.createFilterTabs();
    }

    metricMappingsSearchPredicate(metricMapping: IDisplayDrivingStyleMetricMapping, text: string): boolean {

        if (metricMapping.drivingStyleMetricName.toLocaleLowerCase().indexOf(text) >= 0) {
            return true;
        }

        if (metricMapping.drivingStyleMetricDescription.toLocaleLowerCase().indexOf(text) >= 0) {
            return true;
        }

        return false;
    }

    onFilterBarChange(event: IAggregateFilterEvent<IDisplayDrivingStyleMetricMapping>) {
        if (!event.trigger) {
            return;
        }

        this.filteredMetricMappings = event.filteredData;

        this.sortFilteredMetricMappings();
    }

    onClickToggleMetricMapping(metricMapping: IDisplayDrivingStyleMetricMapping): void {
        if (this.currentMetricMappings?.length >= this.maxAllowedMetrics && !metricMapping.selected) {
            return;
        }

        metricMapping.selected = !metricMapping.selected;

        this.onToggleMetricMapping(metricMapping);
    }

    onToggleMetricMapping(metricMapping: IDisplayDrivingStyleMetricMapping): void {

        if (metricMapping.selected) {
            this.addMetricMappingToCurrent(metricMapping);
            return;
        }

        this.removeMetricMappingFromCurrent(metricMapping);
    }

    onClickMetricMapping(mouseEvent: MouseEvent, metricMapping: IDisplayDrivingStyleMetricMapping): void {
        mouseEvent.stopPropagation();

        if (!metricMapping.selected) {
            return;
        }

        this.metricMappingClick.emit(metricMapping);
    }

    //--

    private createDisplayMetricMappings(): void {
        const newDisplayMetricMappings: IDisplayDrivingStyleMetricMapping[] = [].concat(this.currentMetricMappings);

        newDisplayMetricMappings.forEach(newDisplayMetricMappings => newDisplayMetricMappings.selected = true);

        this.availableMetricMappings?.forEach(availableMetricMapping => {
            if (newDisplayMetricMappings.find(newDisplayMetricMapping => newDisplayMetricMapping.DrivingStyleMetric === availableMetricMapping.DrivingStyleMetric)) {
                return;
            }

            newDisplayMetricMappings.push(copy(availableMetricMapping));
        })

        this.displayMetricMappings = newDisplayMetricMappings;
    }

    private createFilterTabs(): void {
        this.filterTabs = [];

        this.createTabDefinition(this.translationService.t("Common.All"), (r) => true);

        const usedMetricMappingCategories: DrivingStyleMetricCategory[] = [];

        if (!this.availableMetricMappings.length) {
            return;
        }

        this.availableMetricMappings.forEach(metricMapping => {
            if (usedMetricMappingCategories.includes(metricMapping.category) || metricMapping.DrivingStyleMetric === DrivingStyleMetric.Total) {
                return;
            }

            usedMetricMappingCategories.push(metricMapping.category);
        });

        if (!usedMetricMappingCategories.length) {
            return;
        }

        usedMetricMappingCategories.forEach(usedMetricMappingCategory => {
            this.createTabDefinition(this.translationService.tEnum(DrivingStyleMetricCategory, "DrivingStyleMetricCategory", usedMetricMappingCategory), (metricMapping) => metricMapping.category === usedMetricMappingCategory);
        });
    }

    private createTabDefinition(translationString: string, callback: (metricMapping: IDrivingStyleMetricMapping) => boolean): void {
        this.filterTabs.push({ title: translationString, predicate: callback });
    }

    private removeMetricMappingFromCurrent(metricMapping: IDisplayDrivingStyleMetricMapping): void {
        if (!this.currentMetricMappings?.length) {
            return;
        }

        const indexOfMetricMapping = this.currentMetricMappings.findIndex(currentMetricMapping => currentMetricMapping.DrivingStyleMetric === metricMapping.DrivingStyleMetric);

        if (indexOfMetricMapping === -1) {
            return;
        }

        metricMapping.selected = false;

        this.currentMetricMappings.splice(indexOfMetricMapping, 1);
        this.emitChange();
    }

    private addMetricMappingToCurrent(metricMapping: IDisplayDrivingStyleMetricMapping): void {
        if (this.currentMetricMappings.find(currentMetricMapping => currentMetricMapping.DrivingStyleMetric === metricMapping.DrivingStyleMetric)) {
            return;
        }

        const defaultMetricMappingPriorityOptions = this.metricMappingWeightingLevelDetails.find(option => option.isDefault);

        metricMapping.selected = true;
        metricMapping.Weighting = (this.weightingType > 0) ? defaultMetricMappingPriorityOptions.value : 0;
        metricMapping.HighScoreValue = metricMapping.defaults.HighScoreValue;
        metricMapping.LowScoreValue = metricMapping.defaults.LowScoreValue;

        this.currentMetricMappings.push(metricMapping);
        this.emitChange();
    }

    private emitChange(): void {
        this.currentMetricMappings?.forEach(metricMapping => this.drivingStylesService.addWeightingLevelDetailsToMetric(metricMapping, this.weightingType));
        this.currentMetricMappingsChange.emit(this.currentMetricMappings);
    }

    private sortFilteredMetricMappings(): void {
        if (!this.filteredMetricMappings?.length) {
            return;
        }

        this.filteredMetricMappings.sort((metricMapping1, metricMapping2) => metricMapping1.drivingStyleMetricName.localeCompare(metricMapping2.drivingStyleMetricName));
    }
}
